import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';
import { PaginationConst } from '../constants/AppConstant';
import type { StockPlan, StockPlanComment, StockProfile, StockPlanStatus } from '../../models/StockPickTypes';

export interface StockProfileListResponse {
  result: StockProfile[];
  totalCount: number;
  message?: string;
  code: number;
}

type ApiEnvelope<T> = { data: T; message?: string; statusCode: number };

const PROFILES = `${LEAD_API_PREFIX}/capitals/stock-profiles`;
const PLANS = `${LEAD_API_PREFIX}/capitals/stock-plans`;

export async function getStockProfilesPage(
  page: number = 0,
  size: number = PaginationConst.PAGE_SIZE
): Promise<StockProfileListResponse> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: PROFILES,
    params: { page, size, sort: 'createdDate,desc' },
  });
  return response.data as StockProfileListResponse;
}

export async function getStockProfile(id: string): Promise<StockProfile> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PROFILES}/${encodeURIComponent(id)}`,
  });
  return (response.data as ApiEnvelope<StockProfile>).data;
}

export async function createStockProfile(body: StockProfile): Promise<StockProfile> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: PROFILES,
    data: body,
  });
  return (response.data as ApiEnvelope<StockProfile>).data;
}

export async function createStockProfileWithPlan(profile: StockProfile, plan: StockPlan): Promise<StockProfile> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PROFILES}/with-plan`,
    data: { profile, plan },
  });
  return (response.data as ApiEnvelope<StockProfile>).data;
}

export async function updateStockProfile(id: string, body: StockProfile): Promise<StockProfile> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PROFILES}/${encodeURIComponent(id)}`,
    data: { ...body, id },
  });
  return (response.data as ApiEnvelope<StockProfile>).data;
}

export async function deleteStockProfile(id: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PROFILES}/${encodeURIComponent(id)}`,
  });
}

export async function createStockPlan(profileId: string, body: StockPlan): Promise<StockPlan> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PROFILES}/${encodeURIComponent(profileId)}/plans`,
    data: body,
  });
  return (response.data as ApiEnvelope<StockPlan>).data;
}

export async function getStockPlan(planId: string): Promise<StockPlan> {
  const response = await HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PLANS}/${encodeURIComponent(planId)}`,
  });
  return (response.data as ApiEnvelope<StockPlan>).data;
}

export async function updateStockPlan(planId: string, body: StockPlan): Promise<StockPlan> {
  const response = await HttpClient.PUT({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PLANS}/${encodeURIComponent(planId)}`,
    data: { ...body, id: planId },
  });
  return (response.data as ApiEnvelope<StockPlan>).data;
}

export async function updateStockPlanStatus(planId: string, status: StockPlanStatus): Promise<StockPlan> {
  const response = await HttpClient.PATCH({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PLANS}/${encodeURIComponent(planId)}/status`,
    data: { status },
  });
  return (response.data as ApiEnvelope<StockPlan>).data;
}

export async function deleteStockPlan(planId: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PLANS}/${encodeURIComponent(planId)}`,
  });
}

export async function addStockPlanComment(planId: string, body: string): Promise<StockPlanComment> {
  const response = await HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PLANS}/${encodeURIComponent(planId)}/comments`,
    data: { body },
  });
  return (response.data as ApiEnvelope<StockPlanComment>).data;
}

export async function deleteStockPlanComment(planId: string, commentId: string): Promise<void> {
  await HttpClient.DELETE({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${PLANS}/${encodeURIComponent(planId)}/comments/${encodeURIComponent(commentId)}`,
  });
}
